import { Col, Flex, Input, Row, Select, Typography } from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import React, { useCallback, useMemo, useState } from "react";
import { useDebounce } from "react-use";
import { CATEGORY, GENDER_TYPE, LANGUAGE_TYPE, REFER_TYPE } from "../../domain/notes.domain";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import { useFuseSearch } from "../../hooks/useFuseSearch";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import { getAllDcmCodes } from "../../service/client.service";
import { setClientForm, setDsm } from "../../slices/clientSlice";
import type { TClientForm } from "../../types/index.type";
import { getDiagnosisIdsForClientPayload } from "../../utils/datamanipulation.utils";
import SelectableBadge from "../Badge/SelectableBadge.component";
import styles from "./clientform.module.scss";

const formatDobForDisplay = (value?: string | null, age?: number | string) => {
  if (!value) {
    // If no DOB but there's an age, show ~[age] as a placeholder
    if (age !== undefined && age !== null && age !== "") {
      return `~${age}`;
    }
    return "";
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : "";
};

const formatDobInputValue = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const SEARCH_CONFIG = {
  MIN_SEARCH_LENGTH: 2,
  DEFAULT_RESULTS: 50,
  THRESHOLD: 0.1,
} as const;

const ClientForm = () => {
  const componentName = "clientform";
  const dispatch = useAppDispatch();
  const { referIndividualBy, shouldReferAsPatient } = useSpecialityReference();
  const isMobile = useIsMobileView();
  const { clientForm, dsm, isEditing } = useAppSelector((state) => state.client);
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [dobInput, setDobInput] = useState<string>(formatDobForDisplay(clientForm.date_of_birth, clientForm.age));

  // Debounce the search term with 300ms delay for better performance
  useDebounce(
    () => {
      setDebouncedSearchTerm(searchTerm);
    },
    200,
    [searchTerm],
  );

  const updateClientFormWithPatch = useCallback(
    (patch: Partial<TClientForm>) => {
      dispatch(setClientForm({ ...clientForm, ...patch }));
    },
    [dispatch, clientForm],
  );

  const updateClientForm = useCallback(
    (key: keyof TClientForm, value: TClientForm[keyof TClientForm]) => {
      updateClientFormWithPatch({ [key]: value } as Partial<TClientForm>);
    },
    [updateClientFormWithPatch],
  );

  // Memoized search handler to prevent unnecessary re-renders
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const fuseConfig = useMemo(
    () => ({
      data: dsm,
      keys: ["name", "code"],
      threshold: SEARCH_CONFIG.THRESHOLD,
    }),
    [dsm],
  );

  const { search } = useFuseSearch(fuseConfig);

  // Enhanced search with exact match prioritization and minimum query length
  const filteredOptions = useMemo(() => {
    if (!debouncedSearchTerm || debouncedSearchTerm.trim().length < SEARCH_CONFIG.MIN_SEARCH_LENGTH)
      return dsm.slice(0, SEARCH_CONFIG.DEFAULT_RESULTS); // Show first 100 items when no search or query too short
    const fuzzyResults = search(debouncedSearchTerm);
    return fuzzyResults.slice(0, SEARCH_CONFIG.DEFAULT_RESULTS);
  }, [search, debouncedSearchTerm]);

  // Memoize the options array to prevent unnecessary re-renders
  const selectOptions = useMemo(() => {
    return filteredOptions.map((item) => ({
      value: item.id,
      label: `${item.name} (${item.code})`,
    }));
  }, [filteredOptions]);

  const selectedDiagnosisIds = useMemo(() => getDiagnosisIdsForClientPayload(clientForm), [clientForm]);

  const getDsmCodes = () => {
    getAllDcmCodes()
      .then((response) => {
        dispatch(setDsm(response.data));
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const presetReferType = () => {
    if (shouldReferAsPatient) {
      updateClientForm("refer_type", REFER_TYPE.PATIENT);
    } else {
      updateClientForm("refer_type", REFER_TYPE.CLIENT);
    }
  };

  // biome-ignore lint: no need to add getDsmCodes to the dependency array
  React.useEffect(() => {
    // Only fetch DSM codes if not already cached in Redux
    if (dsm.length === 0) {
      getDsmCodes();
    }
    if (!isEditing) {
      presetReferType();
    }
  }, []);

  React.useEffect(() => {
    setDobInput(formatDobForDisplay(clientForm.date_of_birth, clientForm.age));
  }, [clientForm.date_of_birth, clientForm.age]);

  const handleDobInputChange = useCallback((value: string) => {
    setDobInput(formatDobInputValue(value));
  }, []);

  const handleDobFocus = useCallback(() => {
    // Clear the ~age placeholder when user focuses on the field
    if (dobInput.startsWith("~")) {
      setDobInput("");
    }
  }, [dobInput]);

  const handleDobBlur = useCallback(() => {
    // If input is empty, restore the ~age format if there's an age
    if (!dobInput) {
      setDobInput(formatDobForDisplay(clientForm.date_of_birth, clientForm.age));
      return;
    }

    // If input starts with ~, it's the age placeholder - don't try to parse it
    if (dobInput.startsWith("~")) {
      return;
    }

    const parsedDate = dayjs(dobInput, "MM/DD/YYYY", true);
    if (parsedDate.isValid()) {
      setDobInput(parsedDate.format("MM/DD/YYYY"));
      // Only store DOB, don't set age field - age will be calculated from DOB when needed
      updateClientFormWithPatch({ date_of_birth: parsedDate.format("YYYY-MM-DD") });
      return;
    }

    // If input is not valid and not empty, reset to the current state
    setDobInput(formatDobForDisplay(clientForm.date_of_birth, clientForm.age));
  }, [dobInput, clientForm.date_of_birth, clientForm.age, updateClientFormWithPatch]);

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Typography.Text className={`${styles[`${componentName}-form-label`]}`}>Name:</Typography.Text>
        <Input
          defaultValue={clientForm.name}
          onBlur={(e) => updateClientForm("name", e.target.value)}
          placeholder="eg. John Doe or JD"
          className={`${styles[`${componentName}-form-label`]}`}
          maxLength={100}
        />
      </Col>
      <Col span={isMobile ? 24 : 12}>
        <Typography.Text className={`${styles[`${componentName}-form-label`]}`}>MRN (optional):</Typography.Text>
        <Input
          defaultValue={clientForm.mrn || ""}
          onBlur={(e) => updateClientForm("mrn", e.target.value.trim() ? e.target.value.trim() : null)}
          placeholder="eg. 123456"
          maxLength={64}
          className={`${styles[`${componentName}-form-input`]}`}
        />
      </Col>
      <Col span={isMobile ? 24 : 12}>
        <Typography.Text className={`${styles[`${componentName}-form-label`]}`}>
          Date of Birth (optional):
        </Typography.Text>
        <Input
          value={dobInput}
          onChange={(event) => handleDobInputChange(event.target.value)}
          onFocus={handleDobFocus}
          onBlur={handleDobBlur}
          placeholder="MM/DD/YYYY"
          inputMode="numeric"
          maxLength={10}
          className={`${styles[`${componentName}-form-input`]}`}
        />
      </Col>
      <Col span={24} className={`${styles[`${componentName}__modal-row`]}`}>
        <Typography.Text className={`${styles[`${componentName}-form-label`]}`}>Category:</Typography.Text>
        <div className={`${styles[`${componentName}-form-input`]}`}>
          <Flex>
            <SelectableBadge
              count="Individual"
              selected={clientForm.category_type === CATEGORY.INDIVIDUAL}
              onClick={() => updateClientForm("category_type", CATEGORY.INDIVIDUAL)}
            />
            <SelectableBadge
              count="Relationship"
              selected={clientForm.category_type === CATEGORY.RELATIONSHIP}
              onClick={() => updateClientForm("category_type", CATEGORY.RELATIONSHIP)}
            />
            <SelectableBadge
              count="Family"
              selected={clientForm.category_type === CATEGORY.FAMILY}
              onClick={() => updateClientForm("category_type", CATEGORY.FAMILY)}
            />
            <SelectableBadge
              count="Group"
              selected={clientForm.category_type === CATEGORY.GROUP}
              onClick={() => updateClientForm("category_type", CATEGORY.GROUP)}
            />
          </Flex>
        </div>
      </Col>
      <Col span={24}>
        <Typography.Text className={`${styles[`${componentName}-form-label`]}`}>Pronouns:</Typography.Text> <br />
        <div className={`${styles[`${componentName}-form-input`]}`}>
          <Flex>
            <SelectableBadge
              count="Neutral"
              selected={clientForm.gender_type === GENDER_TYPE.NEUTRAL}
              onClick={() => {
                updateClientForm("gender_type", GENDER_TYPE.NEUTRAL);
              }}
            />
            <SelectableBadge
              count="He/Him"
              selected={clientForm.gender_type === GENDER_TYPE.MALE}
              onClick={() => {
                updateClientForm("gender_type", GENDER_TYPE.MALE);
              }}
            />
            <SelectableBadge
              count="She/Her"
              selected={clientForm.gender_type === GENDER_TYPE.FEMALE}
              onClick={() => {
                updateClientForm("gender_type", GENDER_TYPE.FEMALE);
              }}
            />
          </Flex>
        </div>
      </Col>
      <Col span={24}>
        <Typography.Text className={`${styles[`${componentName}-form-label`]}`}>
          Diagnoses - ICD-10 (optional):
        </Typography.Text>
        <br />
        <Select
          mode="multiple"
          value={selectedDiagnosisIds}
          placeholder="Select all diagnoses"
          placement={isMobile ? "topLeft" : "bottomLeft"}
          onChange={(values) => {
            updateClientForm("diagnosis_ids", values);
          }}
          className={`${styles[`${componentName}-form-input`]}`}
          onSearch={handleSearchChange}
          filterOption={false}
          options={selectOptions}
          dropdownRender={(menu) => <div className="client">{menu}</div>}
        />
      </Col>
      <Col span={24}>
        <Typography.Text className={`${styles[`${componentName}-form-label`]}`}>
          Refer to {referIndividualBy} as:
        </Typography.Text>
        <br />
        <div className={`${styles[`${componentName}-form-input`]}`}>
          <Flex>
            <SelectableBadge
              count="Client"
              selected={clientForm.refer_type === REFER_TYPE.CLIENT}
              onClick={() => {
                updateClientForm("refer_type", REFER_TYPE.CLIENT);
              }}
            />
            <SelectableBadge
              count="Patient"
              selected={clientForm.refer_type === REFER_TYPE.PATIENT}
              onClick={() => {
                updateClientForm("refer_type", REFER_TYPE.PATIENT);
              }}
            />
            <SelectableBadge
              count="Individual"
              selected={clientForm.refer_type === REFER_TYPE.INDIVIDUAL}
              onClick={() => {
                updateClientForm("refer_type", REFER_TYPE.INDIVIDUAL);
              }}
            />
            <SelectableBadge
              count="Member"
              selected={clientForm.refer_type === REFER_TYPE.MEMBER}
              onClick={() => {
                updateClientForm("refer_type", REFER_TYPE.MEMBER);
              }}
            />
            <SelectableBadge
              count={`${referIndividualBy} Name`}
              selected={clientForm.refer_type === REFER_TYPE.CLIENT_NAME}
              onClick={() => {
                updateClientForm("refer_type", REFER_TYPE.CLIENT_NAME);
              }}
            />
          </Flex>
        </div>
      </Col>
      <Col span={24}>
        <Typography.Text className={`${styles[`${componentName}-form-label`]}`}>Language:</Typography.Text>
        <br />
        <div className={`${styles[`${componentName}-form-input`]}`}>
          <Flex>
            <SelectableBadge
              count="English"
              selected={clientForm.language_type === LANGUAGE_TYPE.ENGLISH}
              onClick={() => {
                updateClientForm("language_type", LANGUAGE_TYPE.ENGLISH);
              }}
            />
            <SelectableBadge
              count="Spanish"
              selected={clientForm.language_type === LANGUAGE_TYPE.SPANISH}
              onClick={() => {
                updateClientForm("language_type", LANGUAGE_TYPE.SPANISH);
              }}
            />
            <SelectableBadge
              count="Korean"
              selected={clientForm.language_type === LANGUAGE_TYPE.KOREAN}
              onClick={() => {
                updateClientForm("language_type", LANGUAGE_TYPE.KOREAN);
              }}
            />
            <SelectableBadge
              count="Mandarin"
              selected={clientForm.language_type === LANGUAGE_TYPE.MANDARIN}
              onClick={() => {
                updateClientForm("language_type", LANGUAGE_TYPE.MANDARIN);
              }}
            />
            <SelectableBadge
              count="Hindi"
              selected={clientForm.language_type === LANGUAGE_TYPE.HINDI}
              onClick={() => {
                updateClientForm("language_type", LANGUAGE_TYPE.HINDI);
              }}
            />
          </Flex>
        </div>
      </Col>
      <Col span={24}>
        <Typography.Text className={`${styles[`${componentName}-form-label`]}`}>
          {referIndividualBy} Context:
        </Typography.Text>
        <TextArea
          defaultValue={clientForm.context}
          onBlur={(e) => updateClientForm("context", e.target.value)}
          rows={10}
          placeholder="eg. John Doe is a 35-year-old male with a history of depression and anxiety. He is currently taking 10mg of Prozac and 20mg of Xanax daily."
          className={`${styles[`${componentName}-form-label`]}`}
        />
      </Col>
    </Row>
  );
};

export default ClientForm;
